/**
 * DemoModeBanner - Persistent banner shown across all demo views.
 * Includes view-switch button + exit. Always shown when a demo session exists.
 * On first visit per session, also opens a fullscreen modal with a "view-only" notice.
 */
import { useDemoSession } from '@/hooks/useDemoSession';
import { LogOut, Eye, GraduationCap, Users, Info } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const NOTICE_KEY = 'ta_demo_notice_dismissed_v2';

export default function DemoModeBanner() {
  const { isDemo, clear, view, setView } = useDemoSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [noticeOpen, setNoticeOpen] = useState(false);

  useEffect(() => {
    if (!isDemo) return;
    let dismissed = false;
    try { dismissed = sessionStorage.getItem(NOTICE_KEY) === '1'; } catch { /* ignore */ }
    if (!dismissed) setNoticeOpen(true);
  }, [isDemo]);

  // Only show the demo banner on actual /demo/* routes. A leftover demo
  // session in localStorage must NOT make the banner appear on real teacher
  // or student pages — that confused users into thinking the live app was the
  // demo.
  if (!isDemo) return null;
  if (!location.pathname.startsWith('/demo')) return null;

  const handleExit = () => {
    clear();
    navigate('/', { replace: true });
  };

  const isStudent = view === 'student' || location.pathname.startsWith('/demo/student');
  const switchTarget = isStudent ? 'teacher' : 'student';

  const handleSwitch = () => {
    setView(switchTarget);
    navigate(switchTarget === 'student' ? '/demo/student' : '/demo/teacher', { replace: true });
  };

  const handleAcknowledge = () => {
    try { sessionStorage.setItem(NOTICE_KEY, '1'); } catch { /* ignore */ }
    setNoticeOpen(false);
  };

  return (
    <>
      <div
        className="sticky top-0 z-[100] w-full bg-warning border-b-2 border-warning shadow-md"
        style={{ color: 'hsl(var(--warning-foreground))' }}
      >
        <div className="container mx-auto px-2 sm:px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold min-w-0">
            <Eye className="h-4 w-4 shrink-0" />
            <span className="truncate">DEMO-TILA — muutoksia ei tallenneta</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleSwitch}
              className="inline-flex h-8 sm:h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-primary px-2 sm:px-3 text-xs sm:text-sm font-medium shadow-sm transition-colors hover:bg-primary/90"
              style={{ color: 'hsl(0 0% 100%)', WebkitTextFillColor: 'hsl(0 0% 100%)' }}
            >
              {switchTarget === 'student' ? (
                <GraduationCap className="h-4 w-4 shrink-0" style={{ color: 'hsl(0 0% 100%)' }} />
              ) : (
                <Users className="h-4 w-4 shrink-0" style={{ color: 'hsl(0 0% 100%)' }} />
              )}
              <span
                className="hidden sm:inline"
                style={{ color: 'hsl(0 0% 100%)', WebkitTextFillColor: 'hsl(0 0% 100%)' }}
              >
                {switchTarget === 'student' ? 'Vaihda oppilaan näkymään' : 'Vaihda opettajan näkymään'}
              </span>
              <span
                className="sm:hidden"
                style={{ color: 'hsl(0 0% 100%)', WebkitTextFillColor: 'hsl(0 0% 100%)' }}
              >
                {switchTarget === 'student' ? 'Oppilas' : 'Opettaja'}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setNoticeOpen(true)}
              className="inline-flex h-8 sm:h-9 items-center justify-center gap-1.5 rounded-md border border-border bg-primary/80 px-2 sm:px-3 text-xs sm:text-sm font-medium shadow-sm transition-colors hover:bg-primary/70"
              style={{ color: 'hsl(0 0% 100%)', WebkitTextFillColor: 'hsl(0 0% 100%)' }}
              aria-label="Näytä demo-info"
              title="Näytä demo-info"
            >
              <Info className="h-4 w-4 shrink-0" style={{ color: 'hsl(0 0% 100%)' }} />
            </button>
            <button
              type="button"
              onClick={handleExit}
              className="inline-flex h-8 sm:h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-primary px-2 sm:px-3 text-xs sm:text-sm font-medium shadow-sm transition-colors hover:bg-primary/90"
              style={{ color: 'hsl(0 0% 100%)', WebkitTextFillColor: 'hsl(0 0% 100%)' }}
            >
              <LogOut className="h-4 w-4 shrink-0" style={{ color: 'hsl(0 0% 100%)' }} />
              <span
                className="hidden sm:inline"
                style={{ color: 'hsl(0 0% 100%)', WebkitTextFillColor: 'hsl(0 0% 100%)' }}
              >
                Poistu demosta
              </span>
            </button>
          </div>
        </div>
      </div>

      <Dialog open={noticeOpen} onOpenChange={(open) => { if (!open) handleAcknowledge(); }}>
        <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] p-4 sm:p-6 max-h-[90vh] overflow-y-auto rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg pr-6">
              <Info className="h-5 w-5 text-primary shrink-0" />
              <span>Tervetuloa demoon</span>
            </DialogTitle>
            <DialogDescription className="text-sm pt-2 leading-relaxed text-left">
              Tämä on sovelluksen <strong>katseluversio</strong>. Voit tutkia ja
              klikkailla vapaasti, mutta mitään ei tallenneta.
              <br /><br />
              Osa toiminnoista on vielä kesken ja voi antaa virheilmoituksia —
              tämä kuuluu demoon.
              <br /><br />
              Sovellus kehittyy kesän aikana, mutta <strong>perusidea</strong> ei
              muutu. 💙
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2">
            <Button onClick={handleAcknowledge} className="w-full sm:w-auto">
              Selvä, ymmärrän
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
